import { useMutation, useQueryClient } from '@tanstack/react-query'
import { QueryKey } from 'src/reactQuery/enums/queryKey.enum'
import { DataComparisonApiService } from 'src/services/dataComparisonApi'
import {
  DataComparisonCreatePayload,
  DataComparisonListResponse,
} from 'src/services/dataComparisonApi/interfaces/dataComparison.interface'

// Every mutation ends by invalidating the list: the row it created or removed
// is the list's own content.
const useInvalidateComparisons = (campaignId?: string) => {
  const queryClient = useQueryClient()

  return () =>
    queryClient.invalidateQueries({
      queryKey: [QueryKey.GET_DATA_COMPARISONS, campaignId],
    })
}

export const useCreateDataComparisonMutation = (campaignId?: string) => {
  const invalidate = useInvalidateComparisons(campaignId)

  return useMutation({
    mutationFn: (payload: DataComparisonCreatePayload) =>
      DataComparisonApiService.create(campaignId as string, payload),
    onSuccess: invalidate,
  })
}

export const useDeleteDataComparisonMutation = (campaignId?: string) => {
  const queryClient = useQueryClient()
  const queryKey = [QueryKey.GET_DATA_COMPARISONS, campaignId]

  return useMutation({
    mutationFn: (comparisonId: string) =>
      DataComparisonApiService.remove(campaignId as string, comparisonId),

    // Rolled back if the request fails, so a refused delete puts the row back
    // rather than leaving the list claiming something that still exists.
    onError: (_error, _comparisonId, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous)
      }
    },

    // The row leaves the list on click rather than after the round trip. The
    // request can take seconds against a remote database, and waiting for it
    // made Delete look like it had done nothing at all.
    onMutate: async (comparisonId: string) => {
      await queryClient.cancelQueries({ queryKey })

      const previous =
        queryClient.getQueryData<DataComparisonListResponse>(queryKey)

      queryClient.setQueryData<DataComparisonListResponse>(
        queryKey,
        (current) =>
          current && {
            ...current,
            data: current.data.filter(
              (comparison) => comparison.id !== comparisonId
            ),
          }
      )

      return { previous }
    },

    onSettled: () => queryClient.invalidateQueries({ queryKey }),
  })
}
